import { DateTime } from "luxon";
import { type RefObject, useEffect, useRef } from "react";
import type { CalendarPager, CalendarSelection } from "../../components/CustomCalendar/calendarPager";

/** Marks a day's card in the schedule list with its `yyyy-MM-dd` key, so the list can find the selected day and back. */
export const SCHEDULE_DAY_ATTRIBUTE = "data-schedule-day";

/**
 * How far below the list's top edge the selected day sits (px). Leaves room for the week heading above a week's first
 * day, and is the same line the list's scroll reads the current day from, so a jump and a scroll agree on the day.
 */
const SCROLL_ANCHOR_PX = 44;

// The list's own scroll events are ignored while it is being moved for the user, so a jump does not re-select every
// day it passes. `scrollend` ends the wait early; the timeout covers WebViews that don't fire it.
const SMOOTH_SCROLL_SETTLE_MS = 1000;
const INSTANT_SCROLL_SETTLE_MS = 100;

// Within this many px of the end, the list counts as scrolled to the bottom (scroll offsets can be fractional).
const BOTTOM_TOLERANCE_PX = 2;

const DATE_KEY_FORMAT = "yyyy-MM-dd";

type ScheduleListHandlers = {
	onScroll: () => void;
	onScrollEnd: () => void;
};

const isFullyVisible = (element: HTMLElement, container: HTMLElement): boolean => {
	const elementRect = element.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	return elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;
};

/**
 * The day the list is showing: the first day reaching past the anchor line. At the bottom the last days can never
 * reach that line, so there the selected day stays if it is in full view, and otherwise the last day is taken.
 */
const findShownDay = (container: HTMLElement, selectedDateKey: string): HTMLElement | undefined => {
	const days = Array.from(container.querySelectorAll<HTMLElement>(`[${SCHEDULE_DAY_ATTRIBUTE}]`));
	const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - BOTTOM_TOLERANCE_PX;
	if (isAtBottom) {
		const selectedDay = days.find((day) => day.getAttribute(SCHEDULE_DAY_ATTRIBUTE) === selectedDateKey);
		return selectedDay && isFullyVisible(selectedDay, container) ? selectedDay : days.at(-1);
	}
	const anchorLine = container.getBoundingClientRect().top + SCROLL_ANCHOR_PX;
	return days.find((day) => day.getBoundingClientRect().bottom > anchorLine);
};

/**
 * Keeps the schedule list and the calendar on the same day: a selection from the calendar, a page change or Today
 * scrolls the list to that day once, and scrolling the list selects the day it shows. `renderedDays` is whatever the
 * list renders its days from: a scroll whose day hasn't rendered yet waits for it, so a page whose shifts arrive
 * after the selection still lands on it, while a later refetch never pulls the list back.
 */
export const useScheduleListSync = (scrollRef: RefObject<HTMLDivElement | null>, pager: CalendarPager, renderedDays: unknown): ScheduleListHandlers => {
	const ignoreScrollUntil = useRef<number>(0);
	const pendingScroll = useRef<CalendarSelection | null>(null);
	const { selection, followListScroll } = pager;

	useEffect(() => {
		pendingScroll.current = selection.scroll === "none" ? null : selection;
	}, [selection]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: selection and renderedDays only re-try the pending scroll
	useEffect(() => {
		const frameId = requestAnimationFrame(() => {
			const target = pendingScroll.current;
			const container = scrollRef.current;
			if (!target || target.scroll === "none" || !container) return;
			const day = container.querySelector<HTMLElement>(`[${SCHEDULE_DAY_ATTRIBUTE}="${target.date.toFormat(DATE_KEY_FORMAT)}"]`);
			if (!day) return;
			pendingScroll.current = null;
			ignoreScrollUntil.current = performance.now() + (target.scroll === "smooth" ? SMOOTH_SCROLL_SETTLE_MS : INSTANT_SCROLL_SETTLE_MS);
			container.scrollTo({
				top: day.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - SCROLL_ANCHOR_PX,
				behavior: target.scroll,
			});
		});
		return () => cancelAnimationFrame(frameId);
	}, [selection, renderedDays, scrollRef]);

	return {
		onScroll: () => {
			const container = scrollRef.current;
			if (!container || performance.now() < ignoreScrollUntil.current) return;
			const dateKey = findShownDay(container, selection.date.toFormat(DATE_KEY_FORMAT))?.getAttribute(SCHEDULE_DAY_ATTRIBUTE);
			if (dateKey) followListScroll(DateTime.fromFormat(dateKey, DATE_KEY_FORMAT));
		},
		onScrollEnd: () => {
			ignoreScrollUntil.current = 0;
		},
	};
};
